import React from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { BellIcon, BellOffIcon, TrendingUpIcon } from "lucide-react";
import type { OnboardingAnswers, Placement } from "../../../types/onboarding";
import { minuteOptions, reminderOptions } from "../../../data/onboarding";
import { StepHeading } from "../StepHeading";
import { projectNextLevel } from "../../../utils/placement";
import { spring } from "../../../utils/motion";
interface ScheduleStepProps {
  answers: OnboardingAnswers;
  update: (p: Partial<OnboardingAnswers>) => void;
  placement: Placement;
}
export function ScheduleStep({
  answers,
  update,
  placement
}: ScheduleStepProps) {
  const chosen = projectNextLevel(placement, answers.minutes);
  return <div data-id="element-1063" data-mp="2vsze3peuhwx6">
      <StepHeading title="How much time a day?" description="Small and steady beats big and rare. You can change this any time." data-id="element-1064" data-mp="2vspo41tsvprh" />

      <ul className="grid grid-cols-2 gap-3 md:grid-cols-4" role="radiogroup" aria-label="Daily goal" data-id="element-1065" data-mp="11gj022ft5ehr">
        {minuteOptions.map(m => {
        const active = answers.minutes === m.minutes;
        const p = projectNextLevel(placement, m.minutes);
        return <li key={m.minutes} data-id="element-1066" data-mp="17ez5lnru8i91">
              <motion.button type="button" role="radio" aria-checked={active} onClick={() => update({
            minutes: m.minutes
          })} whileTap={{
            scale: 0.97
          }} animate={{
            y: active ? -2 : 0
          }} transition={spring.snappy} className={`focus-ring flex h-full w-full flex-col rounded-card border-3 p-4 text-left transition-colors duration-150 ${active ? "border-line bg-brand-tint shadow-brutal-sm" : "border-line/35 bg-surface hover:border-line/70"}`} data-id="element-1067" data-mp="17g0r6m6hcn50">
                <span className="text-sm font-semibold text-ink-2" data-id="element-1068" data-mp="fpe69ep0yzq2">{m.label}</span>
                <span className="mt-1 text-[34px] font-bold leading-none tracking-[-0.02em] text-ink" data-id="element-1069" data-mp="anoqjlquxxvn">
                  {m.minutes}
                  <span className="ml-1 text-base font-semibold text-ink-2" data-id="element-1070" data-mp="c3stcsxv11v0">min</span>
                </span>
                <span className="mt-auto pt-4 text-xs font-semibold text-ink-2" data-id="element-1071" data-mp="h2m9kt4j76yv">
                  {p.next} by {format(p.date, "MMM yyyy")}
                </span>
              </motion.button>
            </li>;
      })}
      </ul>

      <p className="mt-5 flex items-start gap-2.5 rounded-xl bg-mint px-4 py-3 text-[15px] text-ink" aria-live="polite" data-id="element-1072" data-mp="3pkiczj4pxvgf">
        <TrendingUpIcon className="mt-0.5 h-4 w-4 shrink-0 text-good" aria-hidden="true" data-id="element-1073" data-mp="3uca5hz7fqb97" />
        <span data-id="element-1074" data-mp="3fnng14o3g349">
          At <strong data-id="element-1075" data-mp="3fo70ot54p5lz">{answers.minutes} minutes a day</strong> you could reach <strong data-id="element-1076" data-mp="3kkxf7c1abt5a">{chosen.next}</strong> around{" "}
          <strong data-id="element-1077" data-mp="3kjmc7efnypf8">{format(chosen.date, "MMMM yyyy")}</strong>.
        </span>
      </p>

      <fieldset className="mt-9" data-id="element-1078" data-mp="36mplre3gj9yv">
        <legend className="text-sm font-bold text-ink" data-id="element-1079" data-mp="3bl0t9i6a58dx">When should we nudge you?</legend>
        <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Reminder time" data-id="element-1080" data-mp="3bmtvml9v16g4">
          {reminderOptions.map(r => {
          const active = answers.reminder === r.id;
          const Icon = r.id === "none" ? BellOffIcon : BellIcon;
          return <button key={r.id} type="button" role="radio" aria-checked={active} onClick={() => update({
            reminder: r.id
          })} className={`focus-ring flex items-center gap-2 whitespace-nowrap rounded-xl border-3 px-3.5 py-2 text-sm font-semibold transition-colors duration-150 ${active ? "border-line bg-ink text-paper" : "border-line/30 bg-surface text-ink hover:border-line/70"}`} data-id="element-1081" data-mp="30xsrtop4kq2o">
                <Icon className="h-4 w-4" aria-hidden="true" data-id="element-1082" data-mp="3tob4nhfni5ta" />
                {r.label}
                {r.time && <span className={active ? "text-paper/70" : "text-ink-2"} data-id="element-1083" data-mp="3oprjvkcsn02i">{r.time}</span>}
              </button>;
        })}
        </div>
      </fieldset>
    </div>;
}